import React, { useEffect, useRef, useState } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import ImageLayer from 'ol/layer/Image';
import { Vector as VectorLayer } from 'ol/layer';
import { ImageStatic, Vector as VectorSource } from 'ol/source';
import Feature, { FeatureLike } from 'ol/Feature';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import { Geometry } from 'ol/geom';
import { MapBrowserEvent } from 'ol';
import {
  Select as OLSelect, Draw, Modify, DragBox,
} from 'ol/interaction';
import { platformModifierKeyOnly, never } from 'ol/events/condition';
import {
  Style, Icon,
} from 'ol/style';
import 'ol/ol.css';
import Button from './atoms/Button';
import CustomModal from './atoms/CustomModal';
import Input from './atoms/Input';
import { designTokens } from '../design-system/tokens';

type EditorMode = 'view' | 'add' | 'move' | 'addRow';

const IMAGE_EXTENT: [number, number, number, number] = [-100, -90, 300, 300];

const MapCreator = () => {
  const mapElement = useRef<HTMLDivElement | null>(null);

  const mapRef = useRef<Map | null>(null);
  const imageLayerRef = useRef<ImageLayer<ImageStatic> | null>(null);
  const vectorSourceRef = useRef<VectorSource<Feature<Geometry>> | null>(null);
  const vectorLayerRef = useRef<VectorLayer<
  VectorSource<Feature<Geometry>>
  > | null>(null);
  const selectInteractionRef = useRef<OLSelect | null>(null);
  const drawInteractionRef = useRef<Draw | null>(null);
  const modifyInteractionRef = useRef<Modify | null>(null);
  const dragBoxRef = useRef<DragBox | null>(null);

  const [mode, setMode] = useState<EditorMode>('view');
  const [backgroundUrl, setBackgroundUrl] = useState<string>('/fair_map.png');
  const [selectedPointIds, setSelectedPointIds] = useState<string[]>([]);
  const [exportJson, setExportJson] = useState<string>('');
  const [rowCount, setRowCount] = useState<number>(6);
  const [rowSpacing, setRowSpacing] = useState<number>(20);
  const [editingPoint, setEditingPoint] = useState<{
    feature: Feature<Point>;
    name: string;
    description: string;
    displayRow: number;
    displayCol: number;
  } | null>(null);
  const [batchRow, setBatchRow] = useState<number>(1);
  const [batchCol, setBatchCol] = useState<number>(1);

  const nextIdRef = useRef<number>(1);

  // Helper function to set basic point attributes (minimal setup)
  const setBasicAttributes = (feature: Feature<Point>, id: number) => {
    feature.setId(`P${id}`);
    feature.set('id', `P${id}`);
    // Only set basic attributes - detailed attributes will be set when user edits
    feature.set('name', '');
    feature.set('description', '');
    feature.set('seat_number', 0);
    feature.set('x_coordinate', 0);
    feature.set('y_coordinate', 0);
    feature.set('display_row', 1);
    feature.set('display_col', 1);
    // Default sector color for icon rendering (matches RenderStageMap approach)
    feature.set('sector_color', designTokens.colors.brand.primary[500]);
  };

  useEffect(() => {
    if (!mapElement.current) {
      return () => {};
    }

    // Base image layer
    imageLayerRef.current = new ImageLayer({
      source: new ImageStatic({
        url: backgroundUrl,
        imageExtent: IMAGE_EXTENT,
      }),
    });

    // Vector layer for points
    vectorSourceRef.current = new VectorSource<Feature<Geometry>>();
    vectorLayerRef.current = new VectorLayer({
      source: vectorSourceRef.current,
      declutter: true,
      style: (feature: FeatureLike, resolution: number) => {
        const seatColor = (feature.get && feature.get('sector_color'))
          || designTokens.colors.brand.primary[500];
        const svg = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 6C0 9.31371 2.68629 12 6 12C9.31371 12 12 9.31371 12 6V0H0V6Z" fill="${seatColor}"/>
      </svg>`;
        const iconScale = Math.max(0.4, 0.4 / resolution);
        return new Style({
          image: new Icon({
            anchor: [0.5, 1],
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction',
            src: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
            scale: iconScale,
          }),
        });
      },
    });

    // Create map
    const map = new Map({
      target: mapElement.current,
      layers: [imageLayerRef.current, vectorLayerRef.current],
      view: new View({ projection: 'EPSG:3857', center: [0, 0], zoom: 2 }),
    });
    mapRef.current = map;

    // Selection interaction
    const select = new OLSelect({
      condition: never, // Disable click selection, we'll use box selection
      style: (_: FeatureLike, resolution: number) => new Style({
        image: new Icon({
          anchor: [0.5, 1],
          anchorXUnits: 'fraction',
          anchorYUnits: 'fraction',
          src: '/check-circle.svg',
          scale: Math.max(0.4, 0.4 / resolution),
        }),
      }),
    });
    selectInteractionRef.current = select;
    map.addInteraction(select);

    // Box selection interaction
    const dragBox = new DragBox({
      condition: platformModifierKeyOnly, // Hold Ctrl/Cmd to drag box
    });
    dragBoxRef.current = dragBox;
    map.addInteraction(dragBox);

    dragBox.on('boxend', () => {
      const extent = dragBox.getGeometry().getExtent();
      const source = vectorSourceRef.current;
      if (!source) {
        return;
      }

      const selectedFeatures = select.getFeatures();
      selectedFeatures.clear();

      source.forEachFeatureInExtent(extent, (feature) => {
        selectedFeatures.push(feature);
      });

      const ids = selectedFeatures
        .getArray()
        .map((f: Feature<Geometry>) => String(f.getId() ?? f.get('id') ?? ''));
      setSelectedPointIds(ids);
    });

    // Clear selection when box starts
    dragBox.on('boxstart', () => {
      const selectedFeatures = select.getFeatures();
      selectedFeatures.clear();
      setSelectedPointIds([]);
    });

    // Fit view to image
    requestAnimationFrame(() => {
      map.getView().fit(IMAGE_EXTENT, {
        size: map.getSize(),
        padding: [20, 20, 20, 20],
        maxZoom: 18,
      });
    });

    return () => {
      map.setTarget(undefined);
      mapRef.current = null;
    };
  }, []);

  // Update background image when URL changes
  useEffect(() => {
    if (!imageLayerRef.current || !mapRef.current) {
      return;
    }

    // Create new image source with the updated URL
    const newSource = new ImageStatic({
      url: backgroundUrl,
      imageExtent: IMAGE_EXTENT,
    });

    // Update the image layer source
    imageLayerRef.current.setSource(newSource);

    // Force map to refresh
    mapRef.current.render();
  }, [backgroundUrl]);

  // Manage click handler for editing points (only active in view mode)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return () => {};
    }

    const clickHandler = (evt: MapBrowserEvent<UIEvent>) => {
      const feature = map.forEachFeatureAtPixel(evt.pixel, (f) => f) as Feature<Point>;
      if (feature) {
        setEditingPoint({
          feature,
          name: String(feature.get('name') ?? ''),
          description: String(feature.get('description') ?? ''),
          displayRow: Number(feature.get('display_row') ?? 1),
          displayCol: Number(feature.get('display_col') ?? 1),
        });
      }
    };

    if (mode === 'view') {
      map.on('click', clickHandler);
    }

    return () => {
      map.un('click', clickHandler);
    };
  }, [mode]);

  // Enable/disable interactions based on mode
  useEffect(() => {
    const map = mapRef.current;
    const source = vectorSourceRef.current;
    if (!map || !source) {
      return;
    }

    if (drawInteractionRef.current) {
      map.removeInteraction(drawInteractionRef.current);
      drawInteractionRef.current = null;
    }
    if (modifyInteractionRef.current) {
      map.removeInteraction(modifyInteractionRef.current);
      modifyInteractionRef.current = null;
    }

    // Disable select while drawing to prevent clicks being captured by selection
    if (selectInteractionRef.current) {
      const shouldEnableSelect = mode === 'view' || mode === 'move';
      selectInteractionRef.current.setActive(shouldEnableSelect);
    }

    if (mode === 'add') {
      const draw = new Draw({ source, type: 'Point' });
      draw.on('drawend', (evt) => {
        const feature = evt.feature as Feature<Point>;
        const id = nextIdRef.current;

        setBasicAttributes(feature, id);
        nextIdRef.current += 1;
      });
      drawInteractionRef.current = draw;
      map.addInteraction(draw);
    }

    if (mode === 'move') {
      const selectedFeatures = selectInteractionRef.current?.getFeatures();
      const modify = new Modify({ source });

      // Store original positions when modification starts
      let originalPositions: { [key: string]: [number, number] } = {};
      let draggedFeature: Feature<Geometry> | null = null;
      let dragStartPosition: [number, number] | null = null;

      modify.on('modifystart', (evt) => {
        [draggedFeature] = evt.features.getArray();
        if (draggedFeature) {
          const geom = draggedFeature.getGeometry() as Point;
          dragStartPosition = geom.getCoordinates() as [number, number];

          // Store original positions of all selected features
          originalPositions = {};
          if (selectedFeatures && selectedFeatures.getLength() > 1) {
            selectedFeatures.forEach((feature) => {
              const id = String(feature.getId() ?? feature.get('id') ?? '');
              const geom2 = feature.getGeometry() as Point;
              originalPositions[id] = geom2.getCoordinates() as [number, number];
            });
          }
        }
      });

      modify.on('modifyend', () => {
        if (
          draggedFeature
          && dragStartPosition
          && selectedFeatures
          && selectedFeatures.getLength() > 1
        ) {
          const newGeom = draggedFeature.getGeometry() as Point;
          const newPosition = newGeom.getCoordinates() as [number, number];
          const dx = newPosition[0] - dragStartPosition[0];
          const dy = newPosition[1] - dragStartPosition[1];

          // Move all other selected features by the same offset
          selectedFeatures.forEach((feature) => {
            if (feature !== draggedFeature) {
              const id = String(feature.getId() ?? feature.get('id') ?? '');
              const originalPos = originalPositions[id];
              if (originalPos) {
                const geom = feature.getGeometry() as Point;
                geom.setCoordinates([originalPos[0] + dx, originalPos[1] + dy]);
              }
            }
          });
        }

        // Reset
        draggedFeature = null;
        dragStartPosition = null;
        originalPositions = {};
      });

      modifyInteractionRef.current = modify;
      map.addInteraction(modify);
    }

    if (mode === 'addRow') {
      // Capture current values in closure
      const capturedCount = rowCount;
      const capturedSpacing = rowSpacing;
      console.log(
        'Setting up row draw with count:',
        capturedCount,
        'spacing:',
        capturedSpacing,
      );

      const tempSource = new VectorSource<Feature<Geometry>>();
      const tempLayer = new VectorLayer({
        source: tempSource,
        style: undefined,
      });
      map.addLayer(tempLayer);
      const draw = new Draw({
        source: tempSource,
        type: 'LineString',
        maxPoints: 2,
      });
      draw.on('drawend', (evt) => {
        const line = evt.feature.getGeometry() as LineString;
        const coords = line.getCoordinates();
        const start = coords[0];
        const end = coords[coords.length - 1];
        const dx = end[0] - start[0];
        const dy = end[1] - start[1];
        const len = Math.hypot(dx, dy) || 1;
        const ux = dx / len;
        const uy = dy / len;

        const inputCount = Number.isFinite(capturedCount)
          ? Math.floor(capturedCount)
          : 1;
        const count = Math.max(1, inputCount);
        const inputSpacing = Math.max(
          0,
          Number.isFinite(capturedSpacing) ? capturedSpacing : 0,
        );
        console.log(
          'Creating row with count:',
          count,
          'spacing:',
          inputSpacing,
        );

        if (count === 1) {
          const p = new Feature<Point>({ geometry: new Point(start) });
          const id = nextIdRef.current;
          setBasicAttributes(p, id);
          nextIdRef.current += 1;
          source.addFeature(p);
        } else if (inputSpacing > 0) {
          // Use explicit spacing regardless of segment length (may extend past endpoint)
          for (let i = 0; i < count; i += 1) {
            const offset = i * inputSpacing;
            const px = start[0] + offset * ux;
            const py = start[1] + offset * uy;
            const p = new Feature<Point>({ geometry: new Point([px, py]) });
            const id = nextIdRef.current;

            setBasicAttributes(p, id);
            nextIdRef.current += 1;
            source.addFeature(p);
          }
        } else {
          // Evenly distribute exactly `count` points along the drawn segment
          const step = len / (count - 1);
          for (let i = 0; i < count; i += 1) {
            const offset = i * step;
            const px = start[0] + offset * ux;
            const py = start[1] + offset * uy;
            const p = new Feature<Point>({ geometry: new Point([px, py]) });
            const id = nextIdRef.current;

            setBasicAttributes(p, id);
            nextIdRef.current += 1;
            source.addFeature(p);
          }
        }
        // Clean up the temp feature and layer so no line remains
        tempSource.removeFeature(evt.feature);
        map.removeLayer(tempLayer);
        if (selectInteractionRef.current) {
          selectInteractionRef.current.setActive(mode !== 'addRow');
        }
      });
      drawInteractionRef.current = draw;
      map.addInteraction(draw);
    }
  }, [mode, rowCount, rowSpacing, selectedPointIds]);

  function renumberAll() {
    const source = vectorSourceRef.current;
    if (!source) {
      return;
    }
    const features = source.getFeatures();
    features.forEach((f, index) => {
      const label = String(index + 1);
      f.set('label', label);
      const idString = `P${index + 1}`;
      f.setId(idString);
      f.set('id', idString);
    });
    nextIdRef.current = features.length + 1;
  }

  const handleDeleteSelected = () => {
    const select = selectInteractionRef.current;
    const source = vectorSourceRef.current;
    if (!select || !source) {
      return;
    }
    select.getFeatures().forEach((f) => {
      source.removeFeature(f as Feature<Geometry>);
    });
    select.getFeatures().clear();
    renumberAll();
    setSelectedPointIds([]);
  };

  const handleSelectAll = () => {
    const source = vectorSourceRef.current;
    const select = selectInteractionRef.current;
    if (!source || !select) {
      return;
    }
    const allFeatures = source.getFeatures();
    select.getFeatures().clear();
    allFeatures.forEach((feature) => {
      select.getFeatures().push(feature);
    });
    const ids = allFeatures.map((f) => String(f.getId() ?? f.get('id') ?? ''));
    setSelectedPointIds(ids);
  };

  const handleClearSelection = () => {
    const select = selectInteractionRef.current;
    if (!select) {
      return;
    }
    select.getFeatures().clear();
    setSelectedPointIds([]);
  };

  const handleClearAll = () => {
    vectorSourceRef.current?.clear();
    selectInteractionRef.current?.getFeatures().clear();
    nextIdRef.current = 1;
    setSelectedPointIds([]);
    setExportJson('');
  };

  const handleSavePointEdit = () => {
    if (!editingPoint) {
      return;
    }

    const { feature, displayRow, displayCol } = editingPoint;
    const coords = (feature.getGeometry() as Point).getCoordinates();
    const seatNumber = displayRow * 1000 + displayCol;
    const formattedText = `Fila ${displayRow} -  Butaca ${displayCol}`;

    // Update all attributes
    feature.set('name', formattedText);
    feature.set('description', formattedText);
    feature.set('display_row', displayRow);
    feature.set('display_col', displayCol);
    feature.set('seat_number', seatNumber);
    feature.set('x_coordinate', coords[0]);
    feature.set('y_coordinate', coords[1]);
    feature.set('label', formattedText); // For display

    setEditingPoint(null);
  };

  const handleCancelPointEdit = () => {
    setEditingPoint(null);
  };

  const handleExport = () => {
    const features = vectorSourceRef.current?.getFeatures() ?? [];
    const points = features.map((f) => {
      const geom = f.getGeometry() as Point;
      const [x, y] = geom.getCoordinates();
      return {
        id: String(f.getId() ?? f.get('id') ?? ''),
        name: String(f.get('name') ?? ''),
        description: String(f.get('description') ?? ''),
        seat_number: Number(f.get('seat_number') ?? 0),
        x_coordinate: Number(x ?? 0),
        y_coordinate: Number(y ?? 0),
        display_row: Number(f.get('display_row') ?? 0),
        display_col: Number(f.get('display_col') ?? 0),
        // Keep legacy fields for compatibility
        label: String(f.get('label') ?? ''),
        x: Number(x ?? 0),
        y: Number(y ?? 0),
      };
    });
    const json = JSON.stringify({ backgroundUrl, points }, null, 2);
    setExportJson(json);
    try {
      // Clipboard may fail if not in secure context
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(json).catch(() => {});
      }
    } catch {
      // no-op
    }
  };

  const handleLoad = () => {
    try {
      const parsed = JSON.parse(exportJson) as {
        backgroundUrl?: string;
        points: Array<{ id?: string; label?: string; x: number; y: number }>;
      };
      const source = vectorSourceRef.current;
      if (!source) {
        return;
      }
      source.clear();
      if (parsed.backgroundUrl) setBackgroundUrl(parsed.backgroundUrl);

      let maxId = 0;
      parsed.points.forEach((p: {
        id?: string;
        name?: string;
        description?: string;
        seat_number?: number;
        x_coordinate?: number;
        y_coordinate?: number;
        display_row?: number;
        display_col?: number;
        label?: string;
        x?: number;
        y?: number;
      }) => {
        // Support both new and legacy coordinate formats
        const x = p.x_coordinate ?? p.x ?? 0;
        const y = p.y_coordinate ?? p.y ?? 0;

        const feature = new Feature<Point>({ geometry: new Point([x, y]) });

        // Extract ID number from string ID
        const idString = p.id ?? `P${maxId + 1}`;
        const idMatch = idString.match(/P?(\d+)/);
        const idNum = idMatch ? Number(idMatch[1]) : maxId + 1;
        maxId = Math.max(maxId, idNum);

        // Set all attributes, using new format if available, fallback to legacy or defaults
        feature.setId(idString);
        feature.set('id', idString);
        feature.set('name', p.name ?? p.label ?? String(idNum));
        feature.set('description', p.description ?? `Seat ${p.name ?? p.label ?? idNum}`);
        feature.set('seat_number', p.seat_number ?? idNum);
        feature.set('x_coordinate', x);
        feature.set('y_coordinate', y);
        feature.set('display_row', p.display_row ?? Math.ceil(idNum / 10));
        feature.set('display_col', p.display_col ?? idNum);
        feature.set('label', p.name ?? p.label ?? String(idNum)); // For display

        source.addFeature(feature);
      });
      nextIdRef.current = maxId + 1;
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert('Invalid JSON');
    }
  };

  const handleExportSeatsFile = () => {
    const features = vectorSourceRef.current?.getFeatures() ?? [];
    const seats = features.map((f) => {
      const geom = f.getGeometry() as Point;
      const [x, y] = geom.getCoordinates();
      return {
        name: String(f.get('name') ?? ''),
        description: String(f.get('description') ?? ''),
        seat_number: Number(f.get('seat_number') ?? 0),
        x_coordinate: Number(x ?? 0),
        y_coordinate: Number(y ?? 0),
        display_row: Number(f.get('display_row') ?? 0),
        display_col: Number(f.get('display_col') ?? 0),
      };
    });

    const json = JSON.stringify({ seats }, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'seats.txt';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleApplySameRow = () => {
    const select = selectInteractionRef.current;
    if (!select) {
      return;
    }
    const features = select.getFeatures().getArray() as Feature<Point>[];
    if (features.length === 0) {
      return;
    }
    const rowVal = Math.max(1, Number(batchRow) || 1);
    features.forEach((f) => {
      const colVal = Math.max(1, Number(f.get('display_col')) || 1);
      f.set('display_row', rowVal);
      f.set('seat_number', rowVal * 1000 + colVal);
      const formattedText = `Fila ${rowVal} -  Butaca ${colVal}`;
      f.set('name', formattedText);
      f.set('description', formattedText);
      f.set('label', formattedText);
    });
  };

  const handleApplySameCol = () => {
    const select = selectInteractionRef.current;
    if (!select) {
      return;
    }
    const features = select.getFeatures().getArray() as Feature<Point>[];
    if (features.length === 0) {
      return;
    }
    const colVal = Math.max(1, Number(batchCol) || 1);
    features.forEach((f) => {
      const rowVal = Math.max(1, Number(f.get('display_row')) || 1);
      f.set('display_col', colVal);
      f.set('seat_number', rowVal * 1000 + colVal);
      const formattedText = `Fila ${rowVal} -  Butaca ${colVal}`;
      f.set('name', formattedText);
      f.set('description', formattedText);
      f.set('label', formattedText);
    });
  };

  // Removed auto-columns handler as rows and columns are now set independently

  const fitToPoints = () => {
    const map = mapRef.current;
    const source = vectorSourceRef.current;
    if (!map || !source || source.getFeatures().length === 0) {
      return;
    }
    map.getView().fit(source.getExtent(), {
      size: map.getSize(),
      padding: [20, 20, 20, 20],
      maxZoom: 20,
    });
  };

  const fitToImage = () => {
    const map = mapRef.current;
    if (!map) {
      return;
    }
    map.getView().fit(IMAGE_EXTENT, {
      size: map.getSize(),
      padding: [20, 20, 20, 20],
      maxZoom: 18,
    });
  };

  return (
    <div className="w-full max-w-7xl mx-auto p-6 space-y-6">
      {/* Toolbar Container */}
      <div className="bg-surface-base border border-border-primary rounded-lg p-4 space-y-4">
        {/* Top Row: Mode Selection */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-text-secondary mr-2">Mode:</span>
            <div className="flex gap-1">
              <Button
                variant={mode === 'add' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setMode('add')}
              >
                Add Point
              </Button>
              <Button
                variant={mode === 'move' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setMode('move')}
              >
                Move Points
              </Button>
              <Button
                variant={mode === 'addRow' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setMode('addRow')}
              >
                Add Row
              </Button>
              <Button
                variant={mode === 'view' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setMode('view')}
              >
                View
              </Button>
            </div>
          </div>

          <div className="flex items-center">
            <Input
              id="bg-url"
              label="Background URL"
              type="text"
              value={backgroundUrl}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBackgroundUrl(e.target.value)}
              placeholder="Enter image URL (e.g., /fair_map.png or https://...)"
            />
          </div>
        </div>

        {mode === 'addRow' && (
          <div className="bg-surface-secondary rounded-md p-3">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-text-secondary">Row Settings:</span>
              <div className="flex items-center gap-2">
                <label htmlFor="row-count" className="text-sm text-text-secondary">
                  Count:
                </label>
                <input
                  id="row-count"
                  type="number"
                  min={1}
                  value={rowCount}
                  onChange={(e) => setRowCount(Number(e.target.value))}
                  className="w-20 px-3 py-1 text-sm border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="flex items-center gap-2">
                <label htmlFor="row-spacing" className="text-sm text-text-secondary">
                  Spacing:
                </label>
                <input
                  id="row-spacing"
                  type="number"
                  min={0}
                  value={rowSpacing}
                  onChange={(e) => setRowSpacing(Number(e.target.value))}
                  className="w-24 px-3 py-1 text-sm border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <span className="text-xs text-text-tertiary">
                (0 = evenly spaced along line)
              </span>
            </div>
          </div>
        )}

        {/* Bottom Row: Actions */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-text-secondary mr-1">Actions:</span>
            <Button
              variant="tertiary"
              size="sm"
              onClick={handleDeleteSelected}
              disabled={selectedPointIds.length === 0}
            >
              Delete Selected ({selectedPointIds.length})
            </Button>
            <Button
              variant="tertiary"
              size="sm"
              onClick={handleSelectAll}
            >
              Select All
            </Button>
            <Button
              variant="tertiary"
              size="sm"
              onClick={handleClearSelection}
              disabled={selectedPointIds.length === 0}
            >
              Clear Selection
            </Button>
            <Button
              variant="tertiary"
              size="sm"
              onClick={handleClearAll}
            >
              Clear All
            </Button>
          </div>

          <div className="flex items-center gap-2 ml-4">
            <span className="text-sm font-medium text-text-secondary mr-1">View:</span>
            <Button
              variant="tertiary"
              size="sm"
              onClick={fitToPoints}
            >
              Fit to Points
            </Button>
            <Button
              variant="tertiary"
              size="sm"
              onClick={fitToImage}
            >
              Fit to Image
            </Button>
          </div>
        </div>
      </div>

      <div
        ref={mapElement}
        className="map-container h-[500px] bg-surface-base rounded-lg border border-border-primary"
      />

      <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div>
          <h3 className="text-lg font-semibold text-text-primary mb-2">
            Selected points ({selectedPointIds.length})
          </h3>
          <p className="text-sm text-text-tertiary mb-2">
            Hold Ctrl/Cmd and drag to select multiple points
          </p>
          {selectedPointIds.length > 0 ? (
            <ul className="list-disc list-inside max-h-24 overflow-auto text-sm text-text-secondary">
              {selectedPointIds.map((id) => (
                <li key={id}>{id}</li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-text-tertiary">No points selected</p>
          )}
          <div className="mt-3 flex items-end gap-2">
            <div className="w-32">
              <Input
                id="batch-row"
                label="Set Row for Selection"
                type="number"
                min={1}
                labelAbove
                value={batchRow}
                onChange={(e) => setBatchRow(Number(e.target.value))}
              />
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleApplySameRow}
              disabled={selectedPointIds.length === 0}
            >
              Apply Row
            </Button>
            <div className="w-36">
              <Input
                id="batch-col"
                label="Set Column for Selection"
                type="number"
                min={1}
                labelAbove
                value={batchCol}
                onChange={(e) => setBatchCol(Number(e.target.value))}
              />
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleApplySameCol}
              disabled={selectedPointIds.length === 0}
            >
              Apply Column
            </Button>
          </div>
        </div>

        <div>
          <div className="flex gap-2 items-center mb-2">
            <Button
              variant="tertiary"
              size="sm"
              onClick={handleExport}
            >
              Export JSON
            </Button>
            <Button
              variant="tertiary"
              size="sm"
              onClick={handleExportSeatsFile}
            >
              Export Points JSON File
            </Button>
            <Button
              variant="tertiary"
              size="sm"
              onClick={handleLoad}
            >
              Load JSON
            </Button>
          </div>
          <textarea
            value={exportJson}
            onChange={(e) => setExportJson(e.target.value)}
            placeholder='{"backgroundUrl":"/fair_map.png","points":[{"id":"P1","label":"1","x":0,"y":0}]}'
            className="w-full h-30 font-mono text-xs p-2 border border-border-primary rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
      </div>

      {/* Edit Point Modal */}
      <CustomModal
        isOpen={!!editingPoint}
        onRequestClose={handleCancelPointEdit}
        actionBar={
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={handleCancelPointEdit}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSavePointEdit}>
              Save
            </Button>
          </div>
        }
      >
        <div className="min-w-[400px]">
          <h3 className="text-lg font-semibold text-text-primary mb-4">
            Edit Point Attributes
          </h3>

          <div className="space-y-4">
            <Input
              id="point-name"
              label="Name"
              type="text"
              labelAbove
              value={editingPoint?.name || ''}
              onChange={(e) => setEditingPoint(editingPoint ? { ...editingPoint, name: e.target.value } : null)}
            />

            <Input
              id="point-description"
              label="Description"
              type="text"
              labelAbove
              value={editingPoint?.description || ''}
              onChange={(e) => setEditingPoint(editingPoint ? { ...editingPoint, description: e.target.value } : null)}
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                id="point-row"
                label="Row"
                type="number"
                min={1}
                labelAbove
                value={editingPoint?.displayRow || 1}
                onChange={(e) => setEditingPoint(editingPoint ? {
                  ...editingPoint,
                  displayRow: Number(e.target.value),
                } : null)}
              />
              <Input
                id="point-col"
                label="Column"
                type="number"
                min={1}
                labelAbove
                value={editingPoint?.displayCol || 1}
                onChange={(e) => setEditingPoint(editingPoint ? {
                  ...editingPoint,
                  displayCol: Number(e.target.value),
                } : null)}
              />
            </div>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default MapCreator;
